import { useState, useCallback, useMemo } from 'react';
import { useLocale } from '@repo/i18n';
import type { QuantityInputItem } from '@repo/widget-utils/booking/domains/shared/types';
import {
    createDefaultRooms,
    updateRoomCount,
    updateGuestCount,
    getRoomCountItem,
    getGuestItemsPerRoom,
    getTotalGuests,
    toApiFormat,
    type RoomState,
    type GuestConfig,
} from '@repo/widget-utils/booking/domains/simple/accommodationSearchQuantities';

export type { GuestConfig };

export type UseAccommodationSearchQuantitiesResult = {
    roomCountItems: QuantityInputItem[];
    guestItemsPerRoom: QuantityInputItem[][];
    onChangeRoomCount: (id: string, value: number) => void;
    onChangeGuestCount: (roomIdx: number, id: string, value: number) => void;
    totalGuests: number;
    guests: GuestConfig[];
};

/**
 * Hook for accommodation search quantities
 *
 * @param personsMax - Maximum guests per room (capacity of largest room)
 * @returns Quantity items, handlers, and formatted data
 */
export function useAccommodationSearchQuantities(
    personsMax?: number,
): UseAccommodationSearchQuantitiesResult {
    const { t } = useLocale();

    const [guestsPerRoom, setGuestsPerRoom] = useState<RoomState[]>(createDefaultRooms);
    const roomCount = guestsPerRoom.length;

    const roomCountItems = useMemo(() => [getRoomCountItem(roomCount, t)], [roomCount, t]);

    const guestItemsPerRoom = useMemo(
        () => getGuestItemsPerRoom(guestsPerRoom, personsMax, t),
        [guestsPerRoom, personsMax, t],
    );

    const onChangeRoomCount = useCallback((_id: string, value: number) => {
        setGuestsPerRoom((prev) => updateRoomCount(prev, value));
    }, []);

    const onChangeGuestCount = useCallback((roomIdx: number, id: string, value: number) => {
        if (id !== 'adults' && id !== 'children') return;
        setGuestsPerRoom((prev) => updateGuestCount(prev, roomIdx, id, value));
    }, []);

    const totalGuests = getTotalGuests(guestsPerRoom);

    const guests = useMemo(() => toApiFormat(guestsPerRoom), [guestsPerRoom]);

    return {
        roomCountItems,
        guestItemsPerRoom,
        onChangeRoomCount,
        onChangeGuestCount,
        totalGuests,
        guests,
    };
}
